/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { forwardRef, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { List, ListItem, Button, colors } from '@mui/material';
import Collapse from '@mui/material/Collapse';

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0,
    margin: 0,
  },
  item: {
    display: 'block',
    padding: 0,
    margin: 0,
    width: '100%',
    '&:not(:last-child)': {
      marginBottom: '4px'
    }
  },
  button: {
    color: colors.blueGrey[800],
    padding: '8px 16px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: 500,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    borderRadius: 10,
    margin: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      color: colors.blueGrey[900],
      transform: 'translateX(4px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1.5),
      marginLeft: 0,
      minWidth: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.2rem'
    }
  },
  active: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    backgroundColor: 'rgba(25, 118, 210, 0.15)',
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.2)',
      transform: 'translateX(4px)'
    },
    '& .MuiButton-startIcon': {
      color: theme.palette.primary.main
    }
  },
  nested: {
    paddingLeft: theme.spacing(2),
    paddingTop: '2px',
    '& .MuiListItem-root': {
      padding: 0,
      margin: 0,
      width: '100%',
      '&:not(:last-child)': {
        marginBottom: '1px'
      }
    },
    '& .MuiButton-root': {
      padding: '3px 16px',
      minHeight: 28,
      fontSize: '0.875rem'
    }
  },
}));

const CustomRouterLink = forwardRef((props, ref) => (
  <RouterLink 
    ref={ref}
    {...props} 
    style={{ 
      display: 'flex', 
      alignItems: 'center', 
      width: '100%', 
      textDecoration: 'none',
      color: 'inherit',
      justifyContent: 'flex-start'
    }} 
  />
));

// Function to get icon color based on navigation item title
const getIconColor = (title) => {
  const colorMap = {
    'Dashboard': '#1976d2', // Blue
    'Users': '#388e3c', // Green
    'Customers': '#f57c00', // Orange
    'Restaurants': '#d32f2f', // Red
    'My Restaurant': '#7b1fa2', // Purple
    'Orders': '#1976d2', // Blue
    'Risks': '#d32f2f', // Red
    'Menus': '#388e3c', // Green
    'Transactions': '#f57c00', // Orange
    'Live Transactions': '#ff5722', // Deep Orange
    'Booking': '#3f51b5', // Indigo
    'Booking Sessions': '#3f51b5', // Indigo
    'Bookings': '#3f51b5', // Indigo
    'Reports': '#795548', // Brown
    'Marketing': '#e91e63', // Pink
    'Promotions': '#e91e63', // Pink
    'Vouchers': '#9c27b0', // Purple
    'Account': '#607d8b', // Blue Grey
    'Settings': '#424242', // Grey
    'Staff': '#00796b', // Teal
    'Admin': '#5d4037', // Brown
    'Devices': '#455a64', // Blue Grey
    'Subscriptions': '#8bc34a', // Light Green
    'Timesheets': '#ff9800', // Orange
    'Support': '#00897b', // Teal
    'Support Portal': '#00897b', // Teal
    'Support Admin': '#00695c', // Dark Teal
    'default': '#757575' // Default grey
  };
  return colorMap[title] || colorMap['default'];
};





const SimpleItem = (props) => {
  const { page } = props;
  const classes = useStyles();
  const iconColor = getIconColor(page.title);
  
  // Clone the icon with custom color
  const coloredIcon = React.cloneElement(page.icon, {
    style: { color: iconColor }
  });
  
  return (
    <ListItem
      className={classes.item}
      disableGutters
      key={page.title}
    >
      <Button
        className={classes.button}
        component={CustomRouterLink}
        to={page.href}
        fullWidth
        startIcon={coloredIcon}
        activeClassName={classes.active}
        exact={page.href === '/'}
      >
        {page.title}
      </Button>
    </ListItem>
  );
};


const CollapseItem = (props) => {
  const { page } = props;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const iconColor = getIconColor(page.title);
  
  const handleClick = () => {
    setOpen(!open);
  };

  // Clone the icon with custom color
  const coloredIcon = React.cloneElement(page.icon, {
    style: { color: iconColor }
  });

  return (
    <React.Fragment>
      <ListItem
        className={classes.item}
        disableGutters
        key={page.title}
      >
        <Button
          className={classes.button}
          onClick={handleClick}
          fullWidth
          startIcon={coloredIcon}
          endIcon={open ? '▼' : '▶'}
          sx={{
            '& .MuiButton-endIcon': {
              marginLeft: 'auto',
              fontSize: '0.75rem',
              color: 'inherit'
            }
          }}
        >
          {page.title}
        </Button>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className={classes.nested}>
          {page.children && page.children.map(childPage =>
            <SimpleItem key={childPage.title} page={childPage} />
          )}
        </List>
      </Collapse>
    </React.Fragment>
  );
};

const SidebarNav = props => {
  const { pages, className, ...rest } = props;

  const classes = useStyles();

  return (
    <List
      {...rest}
      className={clsx(classes.root, className)}
    >
      {pages.map(page =>

        page.children.length == 0 ? <SimpleItem key={page.title} page={page} /> : <CollapseItem key={page.title} page={page} />

      )}
    </List>
  );
};

SidebarNav.propTypes = {
  className: PropTypes.string,
  pages: PropTypes.array.isRequired
};

export default SidebarNav;
